import { useQuery } from '@tanstack/react-query';

import { getClaims } from '../../../api/claims/claim.api';
import { useActor } from '../../../components/hooks/useActor';
import { getClaimsSupportContactInfoByLanguage } from '../../../i18n/utils';
import { usePlatformLanguage } from '../../../utils/hooks/usePlatformLanguage';
import { LatestClaimPanel } from './LatestClaimPanel';
import { readClaimHits, selectLatestOpenClaim } from './latestOpenClaim';

const ALL_CLAIMS_PATH = '/customer/claims';

/**
 * Open claims, most recently updated first, so the one we want is the first
 * hit. Selecting client-side from an unordered page would miss a newer open
 * claim that happened to fall outside it. The client-side filter stays as a
 * guard for a response that ignores the status filter.
 *
 * `accountId` is not optional: the endpoint is not scoped by the caller's
 * token, so without it a Cowbell user browsing a policyholder's dashboard is
 * served claims from across every account.
 */
const buildClaimsQuery = (accountId: string) => ({
  accountId,
  f: { status: { in: 'OPEN' } },
  orderBy: 'modified',
  order: 'desc',
  size: 10,
});

/** The dashboard's claims tile: the latest open claim, plus how to report one. */
const ClaimsCard = () => {
  const region = usePlatformLanguage();
  const supportInfo = getClaimsSupportContactInfoByLanguage(region);
  const actor = useActor();
  const accountId = actor.account?.id;

  const query = useQuery(
    // Keyed by account: a static key serves the previous account's claim from
    // cache after a switch.
    ['dashboard-open-claims', accountId],
    () =>
      getClaims({ params: buildClaimsQuery(accountId) }).then(({ data }: any) =>
        selectLatestOpenClaim(readClaimHits(data))
      ),
    { enabled: Boolean(accountId), refetchOnWindowFocus: false }
  );

  return (
    <LatestClaimPanel
      claim={query.data ?? null}
      phone={supportInfo.phone}
      email={supportInfo.email}
      isLoading={query.isInitialLoading}
      isError={query.isError}
      allClaimsHref={ALL_CLAIMS_PATH}
    />
  );
};

export default ClaimsCard;

export interface FileClaimInstructionsProps {
  /** The sentence before the phone number. Callers that already offer the web form lead with a
   *  different opener; everyone else gets the default "To file a claim" copy. The phone/email
   *  markup itself is never duplicated -- only this lead-in varies. */
  readonly leadIn?: string;
}

export const FileClaimInstructions = ({
  leadIn = 'To file a claim, please call',
}: FileClaimInstructionsProps) => {
  const region = usePlatformLanguage();
  const supportInfo = getClaimsSupportContactInfoByLanguage(region);

  return (
    <p
      className="text-center"
      style={{ fontSize: '1.15rem', lineHeight: '1.5rem' }}
    >
      {leadIn}{' '}
      <a className="blue-text" style={{ textDecoration: 'none' }}>
        {supportInfo.phone}
      </a>{' '}
      or email{' '}
      <a
        href={`mailto:${supportInfo.email}`}
        target="_blank"
        rel="noreferrer"
        className="blue-text"
        style={{ textDecoration: 'none' }}
      >
        {supportInfo.email}
      </a>
    </p>
  );
};
